import { useEffect, useState } from 'react';
import { Dialog, DialogContent, Box, Typography, TextField } from '@mui/material';
import { FiAlertTriangle } from 'react-icons/fi';
import Button from 'components/Common/Button';
import { useGetAgencyOrganizations } from '@/apis/agency/organizations/hook';

type StatusVariant = 'inactive' | 'past_due' | 'archived';

interface Props {
  open: boolean;
  status: StatusVariant | null;
  businessName: string;
  onClose: () => void;
  onConfirm: () => void;
}

interface NoticeSection {
  title: string;
  items: string[];
}

interface VariantConfig {
  title: string;
  description: (name: string) => string;
  color: string;
  bg: string;
  whatHappens: (agencyName: string) => NoticeSection;
  notice: NoticeSection;
}

const VARIANTS: Record<StatusVariant, VariantConfig> = {
  inactive: {
    title: 'Inactive',
    description: (name) =>
      `All services for ${name} will get inactive. Customer communication, AI agents, workflows, and integrations are fully stopped for this business.`,
    color: '#FB8703',
    bg: '#FFF3E5',
    whatHappens: (agencyName) => ({
      title: 'What happens next:',
      items: [
        `To restore services, this business must contact ${agencyName} Support.`,
        'No customer activity will be processed while the business is inactive.'
      ]
    }),
    notice: {
      title: 'Warning:',
      items: [
        'After 30 days, this business will move to Archived state and all associated data will be permanently deleted and cannot be recovered.'
      ]
    }
  },
  past_due: {
    title: 'Past Due',
    description: (name) =>
      `All active services for ${name} will be paused due to overdue payment. Account data remains secure and can be restored when services resume.`,
    color: '#D97706',
    bg: '#FFFBEB',
    whatHappens: () => ({
      title: 'What happens next:',
      items: ['Customer communication, AI agents, workflows, and integrations will stop until billing is resolved.']
    }),
    notice: {
      title: 'Billing Note:',
      items: [
        'Existing usage and pending charges will still apply based on the current plan.',
        'If dues are not cleared till next billing, your business will automatically move to Inactive state.'
      ]
    }
  },
  archived: {
    title: 'Archived',
    description: (name) =>
      `All services and data for ${name} will be permanently archived and cannot be recovered, including AI agents, workflows, integrations, knowledge base content, chat logs, call logs, recordings, transcripts, and associated business data.`,
    color: '#DA0808',
    bg: '#FFF2F2',
    whatHappens: () => ({
      title: 'What happens next:',
      items: [
        'This business and all associated platform data will be permanently removed.',
        'All associated telephony services, including assigned phone numbers, call routing configurations, extensions, and communication services connected to agents, will also be permanently removed.'
      ]
    }),
    notice: {
      title: 'Warning:',
      items: [
        'This action is permanent and irreversible.',
        'Once archived, the business cannot be reactivated or recovered.'
      ]
    }
  }
};

export default function BusinessStatusConfirmModal({ open, status, businessName, onClose, onConfirm }: Props) {
  const [value, setValue] = useState('');
  const { data: agencyOrganization } = useGetAgencyOrganizations({ enabled: open });
  const agencyName = agencyOrganization?.name || 'agency';

  useEffect(() => {
    if (!open) setValue('');
  }, [open]);

  if (!status) return null;

  const variant = VARIANTS[status];
  const whatHappens = variant.whatHappens(agencyName);
  const isMatch = value.trim() === businessName.trim() && businessName.trim().length > 0;

  const handleConfirm = () => {
    if (!isMatch) return;
    onConfirm();
    setValue('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' PaperProps={{ sx: { borderRadius: '12px' } }}>
      <DialogContent sx={{ p: { xs: 3, sm: 3 } }}>
        <Box display='flex' justifyContent='center'>
          <Box
            sx={{
              height: 72,
              width: 72,
              borderRadius: '50%',
              backgroundColor: variant.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FiAlertTriangle size={32} color={variant.color} />
          </Box>
        </Box>

        <Typography textAlign='center' fontSize={22} fontWeight={600} mt={2}>
          {variant.title}
        </Typography>

        <Typography textAlign='center' fontSize={14} color='#6B6B6B' lineHeight={1.6} mt={1}>
          {variant.description(businessName)}
        </Typography>

        <Box
          sx={{
            mt: 2.5,
            p: 2,
            borderRadius: '8px',
            backgroundColor: '#F5F5F7'
          }}
        >
          <Typography fontSize={14} fontWeight={600} color='#1F2937'>
            {whatHappens.title}
          </Typography>
          <Box component='ul' sx={{ pl: 2.5, m: 0, mt: 0.5 }}>
            {whatHappens.items.map((item) => (
              <Typography key={item} component='li' fontSize={14} color='#1F2937' lineHeight={1.6}>
                {item}
              </Typography>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            mt: 1.5,
            p: 2,
            borderRadius: '8px',
            backgroundColor: variant.bg
          }}
        >
          <Typography fontSize={14} fontWeight={600} color={variant.color}>
            {variant.notice.title}
          </Typography>
          <Box component='ul' sx={{ pl: 2.5, m: 0, mt: 0.5 }}>
            {variant.notice.items.map((item) => (
              <Typography key={item} component='li' fontSize={14} color={variant.color} lineHeight={1.6}>
                {item}
              </Typography>
            ))}
          </Box>
        </Box>

        <Typography fontSize={14} color='#777777' mt={2.5}>
          Type "<b>{businessName}</b>" to confirm
        </Typography>

        <TextField
          fullWidth
          label='Business name'
          placeholder='Enter Business name'
          value={value}
          onChange={(e) => setValue(e.target.value)}
          sx={{
            mt: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#ECECEC' }
            }
          }}
          InputLabelProps={{ shrink: true }}
        />

        <Box display='flex' justifyContent='flex-end' gap={1.5} mt={3}>
          <Button
            variant='outlined'
            onClick={onClose}
            sx={{
              borderRadius: '24px',
              textTransform: 'none',
              px: 3,
              py: 0.75,
              fontSize: 14,
              borderColor: '#ECECEC',
              color: '#374151',
              '&:hover': {
                borderColor: '#D1D5DB',
                backgroundColor: 'transparent',
                color: '#374151'
              }
            }}
          >
            Cancel
          </Button>

          <Button
            variant='contained'
            disabled={!isMatch}
            onClick={handleConfirm}
            sx={{
              borderRadius: '24px',
              textTransform: 'none',
              px: 3,
              py: 0.75,
              fontSize: 14,
              backgroundColor: variant.color,
              opacity: isMatch ? 1 : 0.5,
              color: '#fff',
              boxShadow: 'none',
              '&:hover': { backgroundColor: variant.color, boxShadow: 'none' },
              '&.Mui-disabled': {
                backgroundColor: variant.color,
                color: '#fff',
                opacity: 0.45
              }
            }}
          >
            Confirm
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
